import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/axios';
import { gerarPDFPecas, gerarExcelPecas, gerarPDFDetalhamento, gerarExcelDetalhamento } from '../utils/pdf';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { calcTotalPago, statusPagamento, pagamentoEmAberto } from '../utils/pagamentos';

const MESES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];
const MESES_CURTOS = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

const STATUS_COR = {
  concluido:            'bg-green-100 text-green-700',
  em_andamento:         'bg-orange-100 text-orange-700',
  aprovado:             'bg-blue-100 text-blue-700',
  aguardando_orcamento: 'bg-purple-100 text-purple-700',
  aguardando_cliente:   'bg-cyan-100 text-cyan-700',
  cancelado:            'bg-red-100 text-red-700',
  orcamento:            'bg-yellow-100 text-yellow-700',
};
const STATUS_LABEL = {
  concluido:            'Concluído',
  em_andamento:         'Em andamento/Em conserto',
  aprovado:             'Aprovado',
  aguardando_orcamento: 'Enviar orçamento p/ aprovação do cliente',
  aguardando_cliente:   'Aguardando cliente',
  cancelado:            'Não Aprovado',
  orcamento:            'Orçamento',
};

const fmt = v => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

// Gráfico de barras SVG
function GraficoBarras({ dados, onClickBarra, mesSelecionadoIdx, empilhado }) {
  const max = Math.max(...dados.map(d => d.valor), 1);
  const H = 90, TOP = 20, barW = 36, gap = 14;
  const AXIS_W = 48;
  const barsW = dados.length * (barW + gap) - gap + 10;
  const svgW = AXIS_W + barsW;
  const svgH = TOP + H + 20;

  const fmtRotulo = v => {
    if (v >= 1000) return `R$${(v / 1000).toFixed(1)}k`;
    return `R$${v.toFixed(0)}`;
  };

  const ticks = [0, 0.33, 0.66, 1];

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full" style={{ maxHeight: 170 }}>
      {ticks.map((t, i) => {
        const lineY = TOP + H - t * H;
        return (
          <g key={i}>
            <line x1={AXIS_W} y1={lineY} x2={svgW} y2={lineY}
              stroke={t === 0 ? '#94a3b8' : '#e2e8f0'} strokeWidth={t === 0 ? 1 : 0.5} />
            <text x={AXIS_W - 4} y={lineY + 3} textAnchor="end" fontSize="7" fill="#94a3b8">
              {fmtRotulo(max * t)}
            </text>
          </g>
        );
      })}

      {dados.map((d, i) => {
        const barH = max > 0 ? Math.max((d.valor / max) * H, d.valor > 0 ? 4 : 0) : 0;
        const x = AXIS_W + i * (barW + gap);
        const barY = TOP + H - barH;
        const selecionado = d.idxMes === mesSelecionadoIdx;
        const cor = selecionado ? '#1d4ed8' : (d.atual ? '#2563eb' : '#93c5fd');
        const pecasRatio = empilhado && d.valor > 0 ? Math.min(d.pecasValor / d.valor, 1) : 0;
        const pecasH = barH * pecasRatio;
        const servicosH = barH - pecasH;
        return (
          <g key={i} style={{ cursor: d.valor > 0 ? 'pointer' : 'default' }}
            onClick={() => d.valor > 0 && onClickBarra && onClickBarra(d)}>
            {selecionado && (
              <rect x={x - 3} y={TOP - 4} width={barW + 6} height={H + 4}
                rx="6" fill="#eff6ff" />
            )}
            {empilhado && d.valor > 0 ? (
              <>
                <clipPath id={`clip-bar-${i}`}>
                  <rect x={x} y={barY} width={barW} height={barH} rx="4" />
                </clipPath>
                <g clipPath={`url(#clip-bar-${i})`}>
                  <rect x={x} y={barY + servicosH} width={barW} height={pecasH} fill="#3b82f6" />
                  <rect x={x} y={barY} width={barW} height={servicosH} fill="#22c55e" />
                </g>
              </>
            ) : (
              <rect x={x} y={barY} width={barW} height={barH}
                rx="4" fill={d.valor > 0 ? cor : '#e2e8f0'} />
            )}
            {d.valor > 0 && (
              <text x={x + barW / 2} y={barY - 4} textAnchor="middle" fontSize="8"
                fontWeight="bold" fill={selecionado ? '#1d4ed8' : (d.atual ? '#1d4ed8' : '#475569')}>
                {fmtRotulo(d.valor)}
              </text>
            )}
            <text x={x + barW / 2} y={TOP + H + 14} textAnchor="middle" fontSize="9"
              fill={selecionado ? '#1d4ed8' : (d.atual ? '#1d4ed8' : '#94a3b8')}
              fontWeight={selecionado || d.atual ? 'bold' : 'normal'}>
              {d.mes}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function DashboardFinanceiro() {
  const navigate = useNavigate();
  const { isProprietario } = useAuth();
  const [servicos, setServicos] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const hoje = new Date();
  const [filtroPeriodo, setFiltroPeriodo] = useState('hoje');
  const [mesSelecionado, setMesSelecionado] = useState(hoje.getMonth());
  const [anoSelecionado, setAnoSelecionado] = useState(hoje.getFullYear());
  const anosDisponiveis = Array.from({ length: 5 }, (_, i) => hoje.getFullYear() - i);

  useEffect(() => {
    Promise.all([api.get('/servicos'), api.get('/configuracao')]).then(([s, c]) => {
      setServicos(s.data);
      setConfig(c.data);
      setLoading(false);
    });
  }, []);

  const inoMes = (s, mes, ano) => {
    const d = new Date(s.updatedAt || s.createdAt);
    return d.getFullYear() === ano && (mes === -1 || d.getMonth() === mes);
  };

  const noPeriodo = s => {
    const d = new Date(s.updatedAt || s.createdAt);
    if (filtroPeriodo === 'hoje') return d.toDateString() === hoje.toDateString();
    if (filtroPeriodo === 'semana') {
      const ini = new Date(hoje); ini.setDate(hoje.getDate() - hoje.getDay() + (hoje.getDay() === 0 ? -6 : 1)); ini.setHours(0,0,0,0);
      const fim = new Date(ini); fim.setDate(ini.getDate() + 6); fim.setHours(23,59,59,999);
      return d >= ini && d <= fim;
    }
    if (filtroPeriodo === 'mes') return inoMes(s, hoje.getMonth(), hoje.getFullYear());
    return inoMes(s, mesSelecionado, anoSelecionado);
  };

  const doMes = servicos.filter(noPeriodo);
  const concluidos = doMes.filter(s => s.status === 'concluido');
  const emAberto = doMes.filter(s => ['orcamento','aprovado','em_andamento','aguardando_orcamento','aguardando_cliente'].includes(s.status));

  const receitaRealizada = concluidos.reduce((a, s) => a + (s.totalGeral || 0), 0);
  const receitaPendente  = emAberto.reduce((a, s) => a + (s.totalGeral || 0), 0);

  // Pagamentos
  const totalRecebido   = doMes.reduce((a, s) => a + calcTotalPago(s), 0);
  const totalAReceber   = doMes.reduce((a, s) => a + Math.max((s.totalGeral || 0) - calcTotalPago(s), 0), 0);
  const FORMAS_PAG = { pix: 'PIX', cartao_credito: 'Cartão de Crédito', cartao_debito: 'Cartão de Débito', dinheiro: 'Dinheiro', cheque: 'Cheque' };
  const FORMAS_ICONE = { pix: '🏦', cartao_credito: '💳', cartao_debito: '💳', dinheiro: '💵', cheque: '📝' };
  const FORMAS_COR = { pix: 'border-teal-400', cartao_credito: 'border-blue-400', cartao_debito: 'border-indigo-400', dinheiro: 'border-green-400', cheque: 'border-yellow-400' };
  const FORMAS_BARRA = { pix: 'bg-teal-400', cartao_credito: 'bg-blue-400', cartao_debito: 'bg-indigo-400', dinheiro: 'bg-green-400', cheque: 'bg-yellow-400' };
  const recebidoPorForma = Object.keys(FORMAS_PAG).map(forma => {
    const total = doMes.reduce((a, s) => {
      return a + (s.pagamentos || []).filter(p => p.forma === forma).reduce((b, p) => b + (p.valor || 0), 0);
    }, 0);
    return { forma, total };
  }).filter(f => f.total > 0);
  const totalPecas       = concluidos.reduce((a, s) => a + (s.totalPecas || 0), 0);
  const totalMaoDeObra   = concluidos.reduce((a, s) => a + (s.totalMaoDeObra || 0), 0);
  const totalDescontos   = concluidos.reduce((a, s) => a + (s.desconto || 0), 0);
  const ticketMedio      = concluidos.length > 0 ? receitaRealizada / concluidos.length : 0;

  // Comparativo mês anterior (só disponível quando um mês específico está selecionado)
  const mesAnt = mesSelecionado === 0 ? 11 : mesSelecionado - 1;
  const anoAnt = mesSelecionado === 0 ? anoSelecionado - 1 : anoSelecionado;
  const receitaMesAnt = mesSelecionado !== -1
    ? servicos.filter(s => inoMes(s, mesAnt, anoAnt) && s.status === 'concluido').reduce((a, s) => a + (s.totalGeral || 0), 0)
    : 0;
  const variacaoPerc = mesSelecionado !== -1 && receitaMesAnt > 0
    ? ((receitaRealizada - receitaMesAnt) / receitaMesAnt * 100) : null;
  const labelComparativo = 'mês anterior';

  // Custo e margem — soma custo de peças (custoUnitario × quantidade) e custo de
  // mão de obra (custoUnitario por serviço, ex.: repasse ao mecânico).
  const calcCustoPecas = s => (s.pecas || []).reduce((a, p) => p.custoUnitario > 0 ? a + p.custoUnitario * p.quantidade : a, 0);
  const calcCustoMao   = s => (s.maosDeObra || []).reduce((a, m) => m.custoUnitario > 0 ? a + m.custoUnitario : a, 0);
  const calcCusto = s => calcCustoPecas(s) + calcCustoMao(s);
  const calcTemCusto = s => (s.pecas || []).some(p => p.custoUnitario > 0) || (s.maosDeObra || []).some(m => m.custoUnitario > 0);
  const totalCustoMes = concluidos.reduce((a, s) => a + calcCusto(s), 0);
  const lucroMes = receitaRealizada - totalCustoMes;
  const margemMes = receitaRealizada > 0 && totalCustoMes > 0 ? (lucroMes / receitaRealizada) * 100 : null;

  // Gráfico últimos 6 meses
  const [filtroGrafico, setFiltroGrafico] = useState('total');
  const [mesBarra, setMesBarra] = useState(null); // { mes, ano, label }

  const dadosGrafico = Array.from({ length: 6 }, (_, i) => {
    const offset = 5 - i;
    let m = mesSelecionado - offset;
    let a = anoSelecionado;
    if (m < 0) { m += 12; a -= 1; }
    const lista = servicos.filter(s => inoMes(s, m, a) && s.status === 'concluido');
    const pecasValor = lista.reduce((acc, s) => acc + (s.totalPecas||0), 0);
    const servicosValor = lista.reduce((acc, s) => acc + (s.totalMaoDeObra||0), 0);
    const valor = filtroGrafico === 'pecas' ? pecasValor : filtroGrafico === 'servicos' ? servicosValor : lista.reduce((acc, s) => acc + (s.totalGeral||0), 0);
    return { mes: MESES_CURTOS[m], valor, pecasValor, servicosValor, atual: i === 5, idxMes: m, ano: a };
  });

  const handleClickBarra = d => {
    if (mesBarra && mesBarra.mes === d.idxMes && mesBarra.ano === d.ano) {
      setMesBarra(null);
    } else {
      setMesBarra({ mes: d.idxMes, ano: d.ano, label: `${MESES[d.idxMes]} ${d.ano}` });
    }
  };

  // Dados do mês clicado na barra
  const concluidosBarra = mesBarra
    ? servicos.filter(s => inoMes(s, mesBarra.mes, mesBarra.ano) && s.status === 'concluido')
    : [];
  const receitaBarra    = concluidosBarra.reduce((a, s) => a + (s.totalGeral || 0), 0);
  const pecasBarra      = concluidosBarra.reduce((a, s) => a + (s.totalPecas || 0), 0);
  const maoBarra        = concluidosBarra.reduce((a, s) => a + (s.totalMaoDeObra || 0), 0);
  const descontoBarra   = concluidosBarra.reduce((a, s) => a + (s.desconto || 0), 0);
  const custoPecasBarra = concluidosBarra.reduce((a, s) => a + calcCustoPecas(s), 0);
  const custoMaoBarra   = concluidosBarra.reduce((a, s) => a + calcCustoMao(s), 0);
  const custoBarra      = custoPecasBarra + custoMaoBarra;
  const lucroBarra      = receitaBarra - custoBarra;
  const margemBarra     = receitaBarra > 0 && custoBarra > 0 ? (lucroBarra / receitaBarra) * 100 : null;

  // Filtro independente de peças trocadas
  const [pecasMes, setPecasMes] = useState(hoje.getMonth());
  const [pecasAno, setPecasAno] = useState(hoje.getFullYear());

  const concluidosPecas = servicos.filter(s => {
    const d = new Date(s.updatedAt || s.createdAt);
    const anoOk = d.getFullYear() === pecasAno;
    const mesOk = pecasMes === -1 || d.getMonth() === pecasMes;
    return anoOk && mesOk && s.status === 'concluido';
  });

  const pecasMap = {};
  concluidosPecas.forEach(s => {
    (s.pecas || []).forEach(p => {
      const key = p.descricao.trim().toLowerCase();
      if (!pecasMap[key]) pecasMap[key] = { descricao: p.descricao, quantidade: 0, valorTotal: 0, ocorrencias: 0 };
      pecasMap[key].quantidade += p.quantidade;
      pecasMap[key].valorTotal += p.valorTotal;
      pecasMap[key].ocorrencias += 1;
    });
  });
  const pecasRanking = Object.values(pecasMap).sort((a, b) => b.quantidade - a.quantidade);
  const anos =[...new Set(servicos.map(s => new Date(s.updatedAt || s.createdAt).getFullYear()))].sort((a, b) => b - a);
  if (!anos.includes(anoSelecionado)) anos.unshift(anoSelecionado);

  // Filtro independente da composição da receita
  const [compMes, setCompMes] = useState(hoje.getMonth());
  const [compAno, setCompAno] = useState(hoje.getFullYear());

  const concluidosComp  = servicos.filter(s => {
    const d = new Date(s.updatedAt || s.createdAt);
    const anoOk = d.getFullYear() === compAno;
    const mesOk = compMes === -1 || d.getMonth() === compMes;
    return anoOk && mesOk && s.status === 'concluido';
  });
  const receitaComp     = concluidosComp.reduce((a, s) => a + (s.totalGeral || 0), 0);
  const pecasComp       = concluidosComp.reduce((a, s) => a + (s.totalPecas || 0), 0);
  const maoComp         = concluidosComp.reduce((a, s) => a + (s.totalMaoDeObra || 0), 0);

  const [buscaPeca, setBuscaPeca] = useState('');
  const [abaPecas, setAbaPecas] = useState(false);
  const [pagPendAberto, setPagPendAberto] = useState(true);
  const semPagamento = servicos.filter(pagamentoEmAberto);
  const [abaTodasOS, setAbaTodasOS] = useState(false);
  const [osConcluidasBarraAberto, setOsConcluidasBarraAberto] = useState(false);

  // ── Relatório por Mecânico ─────────────────────────────────────────────────
  const [relMecAberto, setRelMecAberto] = useState(false);
  const [relMecPeriodo, setRelMecPeriodo] = useState('');
  const [relMecMes, setRelMecMes] = useState(hoje.getMonth());
  const [relMecAno, setRelMecAno] = useState(hoje.getFullYear());

  const noPeriodoMec = s => {
    const d = new Date(s.updatedAt || s.createdAt);
    if (relMecPeriodo === 'hoje') return d.toDateString() === hoje.toDateString();
    if (relMecPeriodo === 'semana') {
      const ini = new Date(hoje); ini.setDate(hoje.getDate() - hoje.getDay() + (hoje.getDay() === 0 ? -6 : 1)); ini.setHours(0,0,0,0);
      const fim = new Date(ini); fim.setDate(ini.getDate() + 6); fim.setHours(23,59,59,999);
      return d >= ini && d <= fim;
    }
    return inoMes(s, relMecMes, relMecAno);
  };

  const relMecDados = (() => {
    const concluidosMec = servicos.filter(s => s.status === 'concluido' && noPeriodoMec(s));
    const mapa = {};
    concluidosMec.forEach(s => {
      const nome = s.responsavel?.trim() || '— Não atribuído';
      if (!mapa[nome]) mapa[nome] = { nome, qtd: 0, totalGeral: 0, totalPecas: 0, totalMao: 0 };
      mapa[nome].qtd += 1;
      mapa[nome].totalGeral += s.totalGeral || 0;
      mapa[nome].totalPecas += s.totalPecas || 0;
      mapa[nome].totalMao   += s.totalMaoDeObra || 0;
    });
    return Object.values(mapa).sort((a, b) => b.totalGeral - a.totalGeral);
  })();
  const pecasFiltradas = buscaPeca
    ? pecasRanking.filter(p => p.descricao.toLowerCase().includes(buscaPeca.toLowerCase()))
    : pecasRanking;

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Carregando...</div>;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header com título + filtros inline — igual ao Painel */}
      <div className="flex items-center gap-3 flex-wrap mb-6 justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Financeiro</h1>
        <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
          {[{ value: 'hoje', label: 'Hoje' }, { value: 'semana', label: 'Semana' }, { value: 'mes', label: 'Mês' }].map(({ value, label }) => (
            <button key={value} type="button" onClick={() => {
              if (filtroPeriodo === value) { setFiltroPeriodo(''); }
              else { setFiltroPeriodo(value); setMesSelecionado(hoje.getMonth()); setAnoSelecionado(hoje.getFullYear()); }
            }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filtroPeriodo === value ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
        <select value={mesSelecionado} onChange={e => { setMesSelecionado(Number(e.target.value)); setMesBarra(null); setFiltroPeriodo(''); }}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
          <option value={-1}>Todos os meses</option>
          {MESES.map((m, i) => <option key={i} value={i}>{m}</option>)}
        </select>
        <select value={anoSelecionado} onChange={e => { setAnoSelecionado(Number(e.target.value)); setFiltroPeriodo(''); }}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
          {anosDisponiveis.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        </div>
      </div>

      {/* Cards principais */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-500">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Faturamento Realizado</p>
          <p className="text-2xl font-bold text-green-600">R$ {fmt(receitaRealizada)}</p>
          {variacaoPerc !== null && (
            <p className={`text-xs mt-1 font-medium ${variacaoPerc >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {variacaoPerc >= 0 ? '▲' : '▼'} {Math.abs(variacaoPerc).toFixed(1)}% vs {labelComparativo}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-1">{concluidos.length} OS concluída(s)</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-orange-400">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Receita Pendente - Serviços em andamento</p>
          <p className="text-2xl font-bold text-orange-500">R$ {fmt(receitaPendente)}</p>
          <p className="text-xs text-gray-400 mt-1">{emAberto.length} OS em aberto</p>
        </div>
      </div>

      {/* Cards de pagamento */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Total Recebido</p>
          <p className="text-xl font-bold text-blue-600">R$ {fmt(totalRecebido)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-400">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">A Receber</p>
          <p className="text-xl font-bold text-red-500">R$ {fmt(totalAReceber)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-gray-300">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Ticket Médio</p>
          <p className="text-xl font-bold text-blue-600">R$ {fmt(ticketMedio)}</p>
        </div>
      </div>

      {/* Recebido por forma de pagamento */}
      {recebidoPorForma.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Recebido por Forma de Pagamento</p>
          <div className="space-y-2.5">
            {recebidoPorForma.map(({ forma, total }) => {
              const pct = totalRecebido > 0 ? (total / totalRecebido) * 100 : 0;
              return (
                <div key={forma} className={`flex items-center gap-3 border-l-4 ${FORMAS_COR[forma]} pl-3`}>
                  <div className="flex items-center gap-1.5 w-28 sm:w-36 shrink-0">
                    <span>{FORMAS_ICONE[forma]}</span>
                    <span className="text-sm text-gray-700 font-medium truncate">{FORMAS_PAG[forma]}</span>
                  </div>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${FORMAS_BARRA[forma]}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-sm font-bold text-gray-800">R$ {fmt(total)}</span>
                    <span className="text-xs text-gray-400 ml-1">({pct.toFixed(0)}%)</span>
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-between border-t border-gray-100 pt-2 mt-1">
              <span className="text-sm font-semibold text-gray-600">Total Recebido</span>
              <span className="text-sm font-bold text-blue-600">R$ {fmt(totalRecebido)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-white rounded-xl shadow-sm p-4 text-center">
          <p className="text-xs text-gray-400 mb-1">Mão de Obra</p>
          <p className="text-base font-bold text-gray-800">R$ {fmt(totalMaoDeObra)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 text-center">
          <p className="text-xs text-gray-400 mb-1">Descontos</p>
          <p className="text-base font-bold text-red-500">- R$ {fmt(totalDescontos)}</p>
        </div>
      </div>

      {/* Material (Peças) */}
      {totalPecas > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-5 border-l-4 border-purple-500">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Material (Peças)</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Valor de Venda</p>
              <p className="text-lg font-bold text-gray-800">R$ {fmt(totalPecas)}</p>
              {receitaRealizada > 0 && (
                <p className="text-xs text-gray-400 mt-0.5">{((totalPecas / receitaRealizada) * 100).toFixed(0)}% do faturamento</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Custo</p>
              <p className="text-lg font-bold text-gray-800">{totalCustoMes > 0 ? `R$ ${fmt(totalCustoMes)}` : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Margem</p>
              <p className="text-lg font-bold text-purple-600">{margemMes !== null ? `${margemMes.toFixed(1)}%` : '—'}</p>
              {totalCustoMes > 0 && <p className="text-xs text-green-600 font-medium">Lucro: R$ {fmt(lucroMes)}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Composição da receita */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-5">
        <div className="flex items-center justify-between mb-3 gap-2">
          <p className="text-sm font-semibold text-gray-700 whitespace-nowrap">Composição da Receita</p>
          <div className="flex gap-2">
            <select className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={compMes} onChange={e => setCompMes(Number(e.target.value))}>
              <option value={-1}>Todos os meses</option>
              {MESES.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={compAno} onChange={e => setCompAno(Number(e.target.value))}>
              {anos.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>
        {receitaComp === 0 ? (
          <p className="text-sm text-gray-400 text-center py-3">Nenhuma OS concluída neste mês</p>
        ) : (
          <>
            <div className="flex rounded-full overflow-hidden h-4 mb-2">
              <div className="bg-blue-500 transition-all" style={{ width: `${(pecasComp / receitaComp) * 100}%` }} />
              <div className="bg-green-500 transition-all" style={{ width: `${(maoComp / receitaComp) * 100}%` }} />
            </div>
            <div className="flex gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"/>
                Peças {((pecasComp / receitaComp) * 100).toFixed(0)}% — R$ {fmt(pecasComp)}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block"/>
                Mão de Obra {((maoComp / receitaComp) * 100).toFixed(0)}% — R$ {fmt(maoComp)}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-2">Total: R$ {fmt(receitaComp)} • {concluidosComp.length} OS concluída(s)</p>
          </>
        )}
      </div>

      {/* Gráfico últimos 6 meses — oculto quando "todos os meses" selecionado */}
      {mesSelecionado !== -1 && <div className="bg-white rounded-xl shadow-sm p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-700">Faturamento — Últimos 6 Meses</p>
          <div className="flex gap-1 bg-gray-100 p-0.5 rounded-lg">
            {[
              { value: 'total',    label: 'Total' },
              { value: 'pecas',    label: 'Material' },
              { value: 'servicos', label: 'Serviço' },
            ].map(op => (
              <button key={op.value} onClick={() => setFiltroGrafico(op.value)}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                  filtroGrafico === op.value
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}>
                {op.label}
              </button>
            ))}
          </div>
        </div>
        <GraficoBarras
          dados={dadosGrafico}
          onClickBarra={handleClickBarra}
          mesSelecionadoIdx={mesBarra?.mes ?? -1}
          empilhado={filtroGrafico === 'total'}
        />
        {filtroGrafico === 'total' && (
          <div className="flex gap-4 text-xs text-gray-500 justify-center mt-1">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"/> Material
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block"/> Serviço
            </span>
          </div>
        )}
        <p className="text-xs text-gray-400 text-center mt-1">
          {mesBarra ? 'Clique novamente para fechar o detalhamento' : 'Clique em uma barra para ver o detalhamento'}
        </p>
      </div>}

      {/* Painel de detalhes do mês clicado */}
      {mesBarra && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-blue-800">Detalhamento — {mesBarra.label}</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => gerarPDFDetalhamento(concluidosBarra, mesBarra.label, config)}
                className="bg-gray-800 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-900 whitespace-nowrap"
              >
                📄 PDF
              </button>
              <button
                onClick={() => gerarExcelDetalhamento(concluidosBarra, mesBarra.label, config)}
                className="bg-green-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-green-800 whitespace-nowrap"
              >
                📊 Excel
              </button>
              <button onClick={() => setMesBarra(null)} className="text-blue-400 hover:text-blue-600 text-xl leading-none ml-1">×</button>
            </div>
          </div>

          {concluidosBarra.length === 0 ? (
            <p className="text-sm text-blue-400 text-center py-2">Nenhuma OS concluída neste mês</p>
          ) : (
            <>
              {/* Cards resumo */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                {/* Linha 1: Faturamento Total | Margem de Lucro Geral */}
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-0.5">Faturamento Total</p>
                  <p className="text-lg font-bold text-green-600">R$ {fmt(receitaBarra)}</p>
                  <p className="text-xs text-gray-400">{concluidosBarra.length} OS concluída(s)</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-0.5">Margem de Lucro Geral</p>
                  {margemBarra !== null ? (
                    <>
                      <p className="text-lg font-bold text-purple-600">{margemBarra.toFixed(1)}%</p>
                      <p className="text-xs text-gray-400">Lucro: R$ {fmt(lucroBarra)}</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 mt-1">Sem custo cadastrado</p>
                  )}
                </div>

                {/* Linha 2: Material (Peças) | Margem sobre Peças */}
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-0.5">Material (Peças)</p>
                  <p className="text-base font-bold text-gray-800">R$ {fmt(pecasBarra)}</p>
                  {receitaBarra > 0 && <p className="text-xs text-gray-400">{((pecasBarra / receitaBarra) * 100).toFixed(0)}% do faturamento</p>}
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-0.5">Margem sobre Peças</p>
                  {custoPecasBarra > 0 && pecasBarra > 0 ? (
                    <>
                      <p className="text-base font-bold text-indigo-600">{(((pecasBarra - custoPecasBarra) / pecasBarra) * 100).toFixed(1)}%</p>
                      <p className="text-xs text-gray-400">Custo: R$ {fmt(custoPecasBarra)}</p>
                      <p className="text-xs text-green-600 font-medium">Lucro: R$ {fmt(pecasBarra - custoPecasBarra)}</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 mt-1">Sem custo cadastrado</p>
                  )}
                </div>

                {/* Linha 3: Mão de Obra | Margem sobre Mão de Obra */}
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-0.5">Mão de Obra</p>
                  <p className="text-base font-bold text-gray-800">R$ {fmt(maoBarra)}</p>
                  {receitaBarra > 0 && <p className="text-xs text-gray-400">{((maoBarra / receitaBarra) * 100).toFixed(0)}% do faturamento</p>}
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-0.5">Margem sobre Mão de Obra</p>
                  {custoMaoBarra > 0 && maoBarra > 0 ? (
                    <>
                      <p className="text-base font-bold text-indigo-600">{(((maoBarra - custoMaoBarra) / maoBarra) * 100).toFixed(1)}%</p>
                      <p className="text-xs text-gray-400">Custo: R$ {fmt(custoMaoBarra)}</p>
                      <p className="text-xs text-green-600 font-medium">Lucro: R$ {fmt(maoBarra - custoMaoBarra)}</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 mt-1">Sem custo cadastrado</p>
                  )}
                </div>

                {/* Linha 4: Ticket Médio | Descontos */}
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-0.5">Ticket Médio</p>
                  <p className="text-base font-bold text-blue-600">R$ {fmt(concluidosBarra.length > 0 ? receitaBarra / concluidosBarra.length : 0)}</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-0.5">Descontos</p>
                  <p className="text-base font-bold text-red-500">- R$ {fmt(descontoBarra)}</p>
                </div>
              </div>

              {/* Lista de OS do mês */}
              <div className="bg-white rounded-lg overflow-hidden">
                <button type="button" onClick={() => setOsConcluidasBarraAberto(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className={`text-lg font-bold text-blue-600 transition-transform ${osConcluidasBarraAberto ? 'rotate-45' : ''}`}>+</span>
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">OS Concluídas</span>
                  </div>
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{concluidosBarra.length}</span>
                </button>
                {osConcluidasBarraAberto && (
                  <div className="divide-y divide-gray-50">
                    {[...concluidosBarra].sort((a, b) => b.totalGeral - a.totalGeral).map(s => {
                      const custo = calcCusto(s);
                      const temCusto = calcTemCusto(s);
                      const lucroOS = s.totalGeral - custo;
                      const margemOS = temCusto && s.totalGeral > 0 ? (lucroOS / s.totalGeral) * 100 : null;
                      return (
                        <div key={s._id} onClick={() => navigate(`/servicos/${s._id}`, { state: { from: '/financeiro' } })}
                          className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 cursor-pointer">
                          <div>
                            <p className="text-sm font-medium text-gray-800">OS #{s.numero} — {s.cliente?.nome}</p>
                            <p className="text-xs text-gray-400">{s.veiculo?.placa} • {s.veiculo?.modelo}</p>
                            <p className="text-xs mt-0.5 text-gray-400">
                              <span>Mat: <span className="text-gray-600 font-medium">R$ {fmt(s.totalPecas)}</span></span>
                              {temCusto && <span className="ml-1">(custo R$ {fmt(custo)})</span>}
                              <span className="mx-1">·</span>
                              <span>Srv: <span className="text-gray-600 font-medium">R$ {fmt(s.totalMaoDeObra)}</span></span>
                            </p>
                            {margemOS !== null && (
                              <p className="text-xs mt-0.5">
                                <span className="text-gray-400">Lucro: </span>
                                <span className={`font-semibold ${lucroOS >= 0 ? 'text-green-600' : 'text-red-500'}`}>R$ {fmt(lucroOS)}</span>
                                <span className={`ml-1.5 font-bold text-xs ${margemOS >= 30 ? 'text-green-600' : margemOS >= 15 ? 'text-yellow-600' : 'text-red-500'}`}>
                                  ({margemOS.toFixed(1)}%)
                                </span>
                              </p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-gray-800">R$ {fmt(s.totalGeral)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Pagamentos Pendentes */}
      {semPagamento.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm mb-4 overflow-hidden border border-orange-200">
          <button type="button" onClick={() => setPagPendAberto(v => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-orange-50 transition-colors">
            <span className={`text-lg font-bold text-orange-500 transition-transform ${pagPendAberto ? 'rotate-45' : ''}`}>+</span>
            <span className="font-semibold text-gray-700">💰 Pagamentos Pendentes</span>
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{semPagamento.length}</span>
          </button>
          {pagPendAberto && (
            <div className="divide-y divide-gray-50">
              {[...semPagamento].sort((a, b) => new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt)).map(s => {
                const parcial = statusPagamento(s) === 'parcial';
                const falta = Math.max((s.totalGeral || 0) - calcTotalPago(s), 0);
                return (
                  <div key={s._id} onClick={() => navigate(`/servicos/${s._id}`, { state: { from: '/financeiro' } })}
                    className="flex items-center justify-between px-4 py-3 hover:bg-orange-50 cursor-pointer">
                    <div>
                      <p className="text-sm font-medium text-gray-800">OS #{s.numero} — {s.cliente?.nome}</p>
                      <p className="text-xs text-gray-400">{s.veiculo?.placa} • {s.veiculo?.modelo}</p>
                      {s.dataConclusao && (
                        <p className="text-xs text-orange-500 mt-0.5">Concluída em {format(new Date(s.dataConclusao), 'dd/MM/yyyy', { locale: ptBR })}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-red-500">R$ {fmt(parcial ? falta : s.totalGeral)}</p>
                      <p className="text-xs text-gray-400">{parcial ? '⚠️ parcial — falta receber' : 'sem pagamento'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Peças trocadas */}
      <div className="bg-white rounded-xl shadow-sm mb-4 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 gap-2">
          <button type="button" onClick={() => setAbaPecas(v => !v)}
            className="flex items-center gap-2 flex-1 text-left">
            <span className={`text-lg font-bold text-blue-600 transition-transform ${abaPecas ? 'rotate-45' : ''}`}>+</span>
            <span className="font-semibold text-gray-700">Peças Trocadas</span>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
              {pecasRanking.reduce((a, p) => a + p.quantidade, 0)} unid.
            </span>
          </button>
          <div className="flex items-center gap-2 flex-shrink-0">
            <select className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={pecasMes} onChange={e => setPecasMes(Number(e.target.value))}>
              <option value={-1}>Todos os meses</option>
              {MESES.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={pecasAno} onChange={e => setPecasAno(Number(e.target.value))}>
              {anos.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            {concluidosPecas.length > 0 && abaPecas && (
              <>
                <button onClick={e => { e.stopPropagation(); gerarPDFPecas(concluidosPecas, pecasMes, pecasAno, config); }}
                  className="bg-gray-800 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-900 whitespace-nowrap">
                  📄 PDF
                </button>
                <button onClick={e => { e.stopPropagation(); gerarExcelPecas(concluidosPecas, pecasMes, pecasAno, config); }}
                  className="bg-green-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-green-800 whitespace-nowrap">
                  📊 Excel
                </button>
              </>
            )}
          </div>
        </div>
        {abaPecas && (
          <div className="px-4 pb-4">
            {pecasRanking.length === 0
              ? <p className="text-gray-400 text-sm text-center py-4">Nenhuma peça no mês</p>
              : <>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Filtrar peça..." value={buscaPeca} onChange={e => setBuscaPeca(e.target.value)} />
                  <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-400 uppercase tracking-wide px-2 mb-1">
                    <span className="col-span-5">Peça</span>
                    <span className="col-span-2 text-center">Qtd</span>
                    <span className="col-span-2 text-center">OS</span>
                    <span className="col-span-3 text-right">Total</span>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {pecasFiltradas.map((p, i) => {
                      const maxQtd = pecasFiltradas[0]?.quantidade || 1;
                      return (
                        <div key={i} className="py-2.5 px-2">
                          <div className="grid grid-cols-12 gap-2 items-center mb-1">
                            <span className="col-span-5 text-sm font-medium text-gray-800 truncate">{p.descricao}</span>
                            <span className="col-span-2 text-center text-sm font-bold text-blue-600">{p.quantidade}</span>
                            <span className="col-span-2 text-center text-xs text-gray-400">{p.ocorrencias}x</span>
                            <span className="col-span-3 text-right text-sm font-semibold text-gray-700">R$ {fmt(p.valorTotal)}</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-400 rounded-full" style={{ width: `${(p.quantidade / maxQtd) * 100}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
            }
          </div>
        )}
      </div>

      {/* Todas as OS do mês */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <button type="button" onClick={() => setAbaTodasOS(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <span className={`text-lg font-bold text-blue-600 transition-transform ${abaTodasOS ? 'rotate-45' : ''}`}>+</span>
            <span className="font-semibold text-gray-700">Todas as OS de {MESES[mesSelecionado]}</span>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{doMes.length}</span>
          </div>
        </button>
        {abaTodasOS && (
          <div className="px-4 pb-4">
            {doMes.length === 0 && <p className="text-gray-400 text-sm text-center py-4">Nenhuma OS neste mês</p>}
            <div className="divide-y divide-gray-50">
              {[...doMes].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)).map(s => {
                const custo = calcCusto(s);
                const temCusto = calcTemCusto(s);
                const lucroOS = s.totalGeral - custo;
                const margemOS = temCusto && s.totalGeral > 0 ? (lucroOS / s.totalGeral) * 100 : null;
                return (
                  <div key={s._id} onClick={() => navigate(`/servicos/${s._id}`, { state: { from: '/financeiro' } })}
                    className="flex items-center justify-between py-3 hover:bg-gray-50 cursor-pointer px-1 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-800">OS #{s.numero} — {s.cliente?.nome}</p>
                      <p className="text-xs text-gray-400">{format(new Date(s.updatedAt), 'dd/MM/yyyy', { locale: ptBR })} • {s.veiculo?.placa}</p>
                      <p className="text-xs mt-0.5 text-gray-400">
                        <span>Mat: <span className="text-gray-600 font-medium">R$ {fmt(s.totalPecas)}</span></span>
                        {temCusto && <span className="ml-1">(custo R$ {fmt(custo)})</span>}
                        <span className="mx-1">·</span>
                        <span>Srv: <span className="text-gray-600 font-medium">R$ {fmt(s.totalMaoDeObra)}</span></span>
                      </p>
                      {margemOS !== null && (
                        <p className="text-xs mt-0.5">
                          <span className="text-gray-400">Lucro: </span>
                          <span className={`font-semibold ${lucroOS >= 0 ? 'text-green-600' : 'text-red-500'}`}>R$ {fmt(lucroOS)}</span>
                          <span className={`ml-1.5 font-bold ${margemOS >= 30 ? 'text-green-600' : margemOS >= 15 ? 'text-yellow-600' : 'text-red-500'}`}>({margemOS.toFixed(1)}%)</span>
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-800">R$ {fmt(s.totalGeral || 0)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COR[s.status] || 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_LABEL[s.status] || s.status}
                      </span>
                      {(() => {
                        const sp = statusPagamento(s);
                        return (
                          <span className={`ml-1 text-xs px-2 py-0.5 rounded-full font-medium ${sp === 'pago' ? 'bg-green-100 text-green-700' : sp === 'parcial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}`}>
                            {sp === 'pago' ? '✅ Pago' : sp === 'parcial' ? '⚠️ Parcial' : '🔴 Pendente'}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {/* ── Relatório por Mecânico (não faz sentido para proprietário solo) ── */}
      {!isProprietario && (
      <div className="bg-white rounded-xl shadow-sm mb-4 overflow-hidden">
        <button
          onClick={() => setRelMecAberto(v => !v)}
          className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        >
          <span className={`text-lg font-bold text-blue-600 transition-transform ${relMecAberto ? 'rotate-45' : ''}`}>+</span>
          <span className="font-semibold text-gray-700">Relatório por Mecânico</span>
        </button>

        {relMecAberto && (
          <div className="border-t border-gray-100 px-4 pb-4 pt-3">
            {/* Filtros */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
                {[{ value: 'hoje', label: 'Hoje' }, { value: 'semana', label: 'Semana' }].map(({ value, label }) => (
                  <button key={value} type="button"
                    onClick={() => {
                      if (relMecPeriodo === value) { setRelMecPeriodo(''); }
                      else { setRelMecPeriodo(value); setRelMecMes(hoje.getMonth()); setRelMecAno(hoje.getFullYear()); }
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${relMecPeriodo === value ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <select
                value={relMecMes}
                onChange={e => { setRelMecMes(Number(e.target.value)); setRelMecPeriodo(''); }}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {MESES.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
              <select
                value={relMecAno}
                onChange={e => { setRelMecAno(Number(e.target.value)); setRelMecPeriodo(''); }}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {anosDisponiveis.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            {relMecDados.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nenhuma OS concluída no período.</p>
            ) : (
              <div className="space-y-3">
                {relMecDados.map(m => {
                  const percPecas = m.totalGeral > 0 ? (m.totalPecas / m.totalGeral) * 100 : 0;
                  const percMao   = m.totalGeral > 0 ? (m.totalMao   / m.totalGeral) * 100 : 0;
                  return (
                    <div key={m.nome} className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-800 text-sm">🔧 {m.nome}</span>
                        <span className="text-xs bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded-full">{m.qtd} OS</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <div className="bg-white rounded-lg px-3 py-2 text-center">
                          <p className="text-xs text-gray-400 mb-0.5">Total</p>
                          <p className="text-sm font-bold text-gray-800">R$ {fmt(m.totalGeral)}</p>
                        </div>
                        <div className="bg-white rounded-lg px-3 py-2 text-center">
                          <p className="text-xs text-gray-400 mb-0.5">Material</p>
                          <p className="text-sm font-bold text-blue-700">R$ {fmt(m.totalPecas)}</p>
                        </div>
                        <div className="bg-white rounded-lg px-3 py-2 text-center">
                          <p className="text-xs text-gray-400 mb-0.5">Serviço</p>
                          <p className="text-sm font-bold text-orange-600">R$ {fmt(m.totalMao)}</p>
                        </div>
                      </div>
                      {/* Barra de composição */}
                      {m.totalGeral > 0 && (
                        <div className="flex rounded-full overflow-hidden h-2 bg-gray-200">
                          <div style={{ width: `${percPecas}%` }} className="bg-blue-500" title={`Material ${percPecas.toFixed(0)}%`} />
                          <div style={{ width: `${percMao}%` }} className="bg-orange-400" title={`Serviço ${percMao.toFixed(0)}%`} />
                        </div>
                      )}
                      <div className="flex gap-3 mt-1.5 text-xs text-gray-400">
                        <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1" />Material {percPecas.toFixed(0)}%</span>
                        <span><span className="inline-block w-2 h-2 rounded-full bg-orange-400 mr-1" />Serviço {percMao.toFixed(0)}%</span>
                        <span className="ml-auto">Ticket médio: R$ {fmt(m.totalGeral / m.qtd)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      )}
      </div>
    </div>
  );
}
